import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Filter,
  BarChart2,
  Clock,
  Wrench,
  Building2,
  AlertTriangle,
  CheckCircle2,
  Timer,
  DollarSign,
  ArrowUp,
  ArrowDown,
  Calendar,
  ListFilter,
  Layout,
  FileText,
  Clipboard,
  Settings,
  BarChart,
  Box,
  ChevronRight,
  AlertOctagon,
  Workflow,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AnalyticsTab from './components/analytics/AnalyticsTab';
import SettingsTab from './components/settings/SettingsTab';

const MaintenanceDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedView, setSelectedView] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Layout },
    { id: 'tickets', label: 'Tickets', icon: Clipboard },
    { id: 'assets', label: 'Assets', icon: Box },
    { id: 'analytics', label: 'Analytics', icon: BarChart },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Mock data - replace with API calls
  const stats = {
    openTickets: 24,
    criticalTickets: 3,
    avgResponseTime: '2.5 hrs',
    completionRate: 92,
    monthlySpend: 45750,
    ticketTrend: '+12%',
    efficiency: 87,
  };

  const tickets = [
    {
      id: "MT-001",
      title: "AC Unit Malfunction",
      location: "Store #123 - Dining Area",
      priority: "high",
      status: "in-progress",
      type: "HVAC",
      submittedDate: "2024-03-15",
      assignedTo: "Mike Tech",
      estimatedCompletion: "2024-03-17"
    },
    {
      id: "MT-002",
      title: "Kitchen Sink Leakage",
      location: "Store #145 - Kitchen",
      priority: "medium",
      status: "pending",
      type: "Plumbing",
      submittedDate: "2024-03-15",
      assignedTo: "John Smith",
      estimatedCompletion: "2024-03-16"
    },
    // Add more mock tickets...
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
      critical: 'bg-purple-100 text-purple-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);

    const relativeTime = diffDays === 0 
      ? 'Today' 
      : diffDays === 1 
      ? 'Tomorrow' 
      : `In ${diffDays} days`;

    return { formattedDate, relativeTime };
  };

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="bg-green-800 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Maintenance Management</h1>
            <p className="text-green-100 mt-1">Track and manage maintenance requests, assets, and performance</p>
          </div>
          <Link
            to="new-ticket"
            className="px-4 py-2 bg-white text-green-800 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Ticket
          </Link>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-green-800'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Open Tickets */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Open Tickets</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.openTickets}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-green-600">
                    <ArrowUp className="w-4 h-4" />
                    <span className="text-sm ml-1">12%</span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-blue-500 rounded-full" style={{ width: '65%' }} />
                  </div>
                </div>
              </motion.div>

              {/* Response Time */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Timer className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Avg Response Time</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-green-600">
                    <ArrowDown className="w-4 h-4" />
                    <span className="text-sm ml-1">8%</span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-green-500 rounded-full" style={{ width: '78%' }} />
                  </div>
                </div>
              </motion.div>

              {/* Monthly Spend */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Monthly Spend</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${stats.monthlySpend.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-red-600">
                    <ArrowUp className="w-4 h-4" />
                    <span className="text-sm ml-1">5%</span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-purple-500 rounded-full" style={{ width: '45%' }} />
                  </div>
                </div>
              </motion.div>

              {/* Completion Rate */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <CheckCircle2 className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Completion Rate</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.completionRate}%</p>
                    </div>
                  </div>
                  <div className="flex items-center text-green-600">
                    <ArrowUp className="w-4 h-4" />
                    <span className="text-sm ml-1">3%</span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-yellow-500 rounded-full" style={{ width: '92%' }} />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Recent Tickets Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-600 rounded-xl">
                      <Clipboard className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Recent Tickets</h2>
                      <p className="text-sm text-gray-500">Monitor and manage maintenance requests</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="p-2 hover:bg-gray-50 rounded-lg text-gray-500 hover:text-gray-600">
                      <Filter className="w-5 h-5" />
                    </button>
                    <button className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors">
                      View All Tickets
                    </button>
                  </div>
                </div>
              </div>

              {/* Filter Pills */}
              <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2">
                <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-md">
                  All Tickets (24)
                </span>
                <span className="px-3 py-1 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md cursor-pointer">
                  High Priority (3)
                </span>
                <span className="px-3 py-1 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md cursor-pointer">
                  In Progress (8)
                </span>
                <span className="px-3 py-1 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md cursor-pointer">
                  Pending (13)
                </span>
              </div>

              {/* Tickets List */}
              <div className="divide-y divide-gray-100">
                {tickets.map((ticket) => (
                  <motion.div
                    key={ticket.id}
                    className="group"
                    initial={false}
                  >
                    {/* Main Ticket Row */}
                    <div className="px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-gray-50/80 cursor-pointer">
                      {/* Priority & ID */}
                      <div className="col-span-2 flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center border
                          ${ticket.priority === 'high' 
                            ? 'bg-red-50 border-red-200' 
                            : ticket.priority === 'medium'
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          {ticket.priority === 'high' ? (
                            <AlertOctagon className="w-4 h-4 text-red-600" />
                          ) : ticket.priority === 'medium' ? (
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Wrench className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-gray-900">{ticket.id}</span>
                          <span className={`text-xs font-medium block ${
                            ticket.priority === 'high' 
                              ? 'text-red-700' 
                              : ticket.priority === 'medium'
                              ? 'text-amber-700'
                              : 'text-blue-700'
                          }`}>
                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                          </span>
                        </div>
                      </div>

                      {/* Title & Type */}
                      <div className="col-span-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {ticket.title}
                        </h3>
                        <span className="text-xs text-gray-500">{ticket.type}</span>
                      </div>

                      {/* Location */}
                      <div className="col-span-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-violet-500" />
                        <span className="text-sm font-medium text-gray-700">
                          {ticket.location.split('-')[0].trim()}
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className="col-span-2 flex justify-center">
                        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset
                          ${ticket.status === 'in-progress' 
                            ? 'bg-blue-50/50 text-blue-700 ring-blue-700/10' 
                            : ticket.status === 'pending'
                            ? 'bg-amber-50/50 text-amber-700 ring-amber-700/10'
                            : 'bg-green-50/50 text-green-700 ring-green-700/10'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1 h-1 rounded-full ${
                              ticket.status === 'in-progress' ? 'bg-blue-500 animate-pulse' :
                              ticket.status === 'pending' ? 'bg-amber-500' : 'bg-green-500'
                            }`} />
                            {ticket.status.split('-').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </div>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="col-span-2">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500">Submitted</span>
                              <span className="text-sm font-medium text-gray-700">
                                {formatDate(ticket.submittedDate).formattedDate}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Timer className="w-4 h-4 text-orange-500" />
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500">Due</span>
                              <span className="text-sm font-medium text-gray-700">
                                {formatDate(ticket.estimatedCompletion).formattedDate}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button 
                          onClick={() => navigate(`/maintenance/ticket/${ticket.id}`)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                        >
                          <FileText className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                        </button>
                        <button 
                          onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                          className={`p-1.5 rounded-lg transition-colors group
                            ${expandedTicket === ticket.id 
                              ? 'bg-blue-50 text-blue-600' 
                              : 'hover:bg-gray-100'
                            }`}
                        >
                          <Workflow className={`w-4 h-4 ${
                            expandedTicket === ticket.id 
                              ? 'text-blue-600' 
                              : 'text-gray-400 group-hover:text-blue-600'
                          }`} />
                        </button>
                      </div>
                    </div>

                    {/* Expandable Workflow Section */}
                    <AnimatePresence>
                      {expandedTicket === ticket.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden border-t border-gray-100"
                        >
                          <div className="p-4 bg-gray-50/50">
                            {/* Workflow Steps */}
                            <div className="relative flex justify-between px-8 mb-20">
                              {/* Background Line - Further adjusted */}
                              <div className="absolute left-[30px] right-[30px] top-[20px] h-[2px] bg-gray-200" />
                              
                              {[
                                { 
                                  status: 'completed', 
                                  label: 'Submitted',
                                  date: '15 Mar, 9:30 AM',
                                  icon: FileText,
                                  user: 'John Manager',
                                  role: 'Store Manager'
                                },
                                { 
                                  status: 'completed', 
                                  label: 'Approved',
                                  date: '15 Mar, 10:15 AM',
                                  icon: CheckCircle2,
                                  user: 'Sarah Admin',
                                  role: 'Regional Admin'
                                },
                                { 
                                  status: 'completed', 
                                  label: 'Accepted',
                                  date: '15 Mar, 11:00 AM',
                                  icon: CheckCircle2,
                                  user: 'Mike Tech',
                                  role: 'Maintenance Lead'
                                },
                                { 
                                  status: 'completed', 
                                  label: 'Assigned',
                                  date: '15 Mar, 11:30 AM',
                                  icon: Users,
                                  user: 'Mike Tech',
                                  role: 'Maintenance Lead'
                                },
                                { 
                                  status: 'completed', 
                                  label: 'Work Started',
                                  date: '15 Mar, 1:00 PM',
                                  icon: Wrench,
                                  user: 'Alex Tech',
                                  role: 'Technician'
                                },
                                { 
                                  status: 'current', 
                                  label: 'In Progress',
                                  date: '15 Mar, 2:00 PM',
                                  icon: Timer,
                                  user: 'Alex Tech',
                                  role: 'Technician'
                                },
                                { 
                                  status: 'pending', 
                                  label: 'Work Done',
                                  date: 'Pending',
                                  icon: CheckCircle2
                                },
                                { 
                                  status: 'pending', 
                                  label: 'Verification',
                                  date: 'Pending',
                                  icon: AlertTriangle
                                },
                                { 
                                  status: 'pending', 
                                  label: 'Completed',
                                  date: 'Pending',
                                  icon: CheckCircle2
                                }
                              ].map((step, index, array) => {
                                const Icon = step.icon;
                                const isLast = index === array.length - 1;
                                
                                return (
                                  <div key={index} className="relative flex-1">
                                    {/* Progress Line - Updated to green-800 */}
                                    {!isLast && (
                                      <motion.div 
                                        initial={{ width: 0 }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        className={`absolute left-[35px] w-[calc(100% - 35px)] h-[2px] top-[20px]
                                          ${step.status === 'completed' ? 'bg-green-800' : 'bg-transparent'}`} 
                                      />
                                    )}
                                    
                                    {/* Step Circle with Animation - Updated completed state color */}
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      transition={{ 
                                        type: "spring",
                                        stiffness: 260,
                                        damping: 20,
                                        delay: index * 0.1 
                                      }}
                                      className="relative z-10 w-10"
                                    >
                                      <div className={`relative w-10 h-10 rounded-lg flex items-center justify-center
                                        ${step.status === 'completed'
                                          ? 'bg-green-800 shadow-sm'
                                          : step.status === 'current'
                                          ? 'bg-blue-500 shadow-sm'
                                          : 'bg-white border border-gray-200'
                                        }`}
                                      >
                                        <Icon className={`w-4 h-4 
                                          ${step.status === 'completed' || step.status === 'current'
                                            ? 'text-white' 
                                            : 'text-gray-400'
                                          }`} 
                                        />

                                        {/* Pulse Effect for Current Step */}
                                        {step.status === 'current' && (
                                          <div className="absolute -inset-1 rounded-lg">
                                            <motion.div
                                              animate={{
                                                scale: [1, 1.2, 1.2],
                                                opacity: [0.4, 0, 0]
                                              }}
                                              transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: ["easeOut", "linear"],
                                                times: [0, 0.3, 1]
                                              }}
                                              className="absolute inset-0 rounded-lg bg-blue-500/40"
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {/* Step Info - Updated completed state text color */}
                                      <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-28 text-center">
                                        <p className={`text-xs font-medium mb-1
                                          ${step.status === 'completed' 
                                            ? 'text-green-800'
                                            : step.status === 'current'
                                            ? 'text-blue-600'
                                            : 'text-gray-400'
                                          }`}
                                        >
                                          {step.label}
                                        </p>
                                        {(step.status === 'completed' || step.status === 'current') && (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <p className="text-[10px] text-gray-600 font-medium">
                                              {step.user}
                                            </p>
                                            <p className="text-[9px] text-gray-400">
                                              {step.date}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Quick Info - More Compact */}
                            <div className="mt-8 flex justify-between px-8">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-blue-500" />
                                <span className="text-xs text-gray-600">Started: 15 Mar, 9:30 AM</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Timer className="w-4 h-4 text-blue-500" />
                                <span className="text-xs text-gray-600">Est. Completion: 17 Mar, 5:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-500" />
                                <span className="text-xs text-gray-600">Assigned: Mike Tech</span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'tickets' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Tickets specific view */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">All Tickets</h2>
                {/* ... existing filter controls ... */}
              </div>
            </div>
            {/* ... existing table content ... */}
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Asset Management</h2>
            {/* Add asset management content */}
          </div>
        )}

        {activeTab === 'analytics' && <AnalyticsTab />}

        {activeTab === 'reports' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Reports</h2>
            {/* Add reports content */}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <SettingsTab user={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceDashboard;
